/**
 * The /overview reads (spec §2.2), on the browser client (the caller's RLS).
 * Facts and summaries THROW on failure (their boxes fail, never shrink); the
 * mapping and the app widgets never reject, because a failure is itself an
 * answer there (FAILED_MAPPING; a failed box).
 *
 * @module services/overview/overviewLoaders
 */
import { getSupabaseClient } from '@/config';
import { readCampaignSummary } from '@/services/mediaIntelligenceShared';
import { errorMessageOf, readAllRows } from '@/services/supabasePaging';
import { fetchResultMappings } from '@/services/targets/targetsService';
import { currentAwareness, FAILED_MAPPING, workspaceMapping, type ResultMappingInfo } from '@/services/targets/resultMetric';
import { APP_LOADERS, type AppLoaderContext } from '@/services/looker/lookerAppLoaders';
import type { AppWidget } from '@/services/looker/lookerAppTiles';
import type { AppTileState } from '@/hooks/useLookerAppTiles';
import { logError } from '@/services/logger';
import { readOverviewFacts } from './overviewFacts';
import type { FactPair, OverviewWindows } from './overviewTypes';
import type { SummaryReads } from './topCampaigns';

/** R1: the current and the previous window; Result's cut windows are client filters over these. */
export const loadFactPair = async (workspaceIds: readonly string[], w: OverviewWindows): Promise<FactPair> => {
  const [current, previous] = await Promise.all([readOverviewFacts(workspaceIds, w.current), readOverviewFacts(workspaceIds, w.previous)]);
  return { current, previous };
};

/** R3: campaign → account, paged by id (the only unique key); only the two columns are selected. */
const readCampaignAccounts = async (workspaceIds: readonly string[]): Promise<Record<string, string | null>> => {
  const { rows, error } = await readAllRows<{ id: string; account_id: string | null }>(
    (lo, hi) => getSupabaseClient().from('media_campaigns').select('id, account_id')
      .in('workspace_id', [...workspaceIds]).order('id', { ascending: true }).range(lo, hi),
    'overview campaign accounts',
  );
  if (error) throw new Error(`overview: campaign accounts read failed: ${errorMessageOf(error)}`);
  return Object.fromEntries(rows.map((r) => [r.id, r.account_id]));
};

/** R2: box 13's spend ranking (current), its Result (cut; skipped when no day qualifies), and the currency link. */
export const loadSummaries = async (workspaceIds: readonly string[], w: OverviewWindows): Promise<SummaryReads> => {
  const scope = { workspaceIds: [...workspaceIds] };
  const [current, cut, campaignAccount] = await Promise.all([
    readCampaignSummary(scope, w.current.start, w.current.end, 'overview summary'),
    w.cut ? readCampaignSummary(scope, w.cut.start, w.cut.end, 'overview summary cut') : Promise.resolve({ rows: [], error: null }),
    readCampaignAccounts(workspaceIds),
  ]);
  if (current.error) throw new Error(`overview: campaign summary read failed: ${errorMessageOf(current.error)}`);
  if (cut.error) throw new Error(`overview: cut campaign summary read failed: ${errorMessageOf(cut.error)}`);
  return { current: current.rows, cut: cut.rows, campaignAccount };
};

/** R6: never rejects. A failed read is FAILED_MAPPING (Result "—", targets failed), never Impressions. */
export const loadMapping = async (workspaceId: string): Promise<ResultMappingInfo> => {
  try {
    return workspaceMapping(currentAwareness(await fetchResultMappings(workspaceId)));
  } catch (err) {
    logError('OverviewMapping', 'result mapping read failed', err);
    return { ...FAILED_MAPPING };
  }
};

/** R11: the /looker app-box loaders as they are; each widget settles on its own and never rejects. */
export const settleWidgets = async (
  widgets: readonly AppWidget[], ctx: AppLoaderContext,
): Promise<Partial<Record<AppWidget, AppTileState>>> => {
  const settled = await Promise.allSettled(widgets.map((w) => Promise.resolve().then(() => APP_LOADERS[w](ctx))));
  return Object.fromEntries(widgets.map((w, i): [AppWidget, AppTileState] => {
    const r = settled[i];
    if (r.status === 'fulfilled') return [w, { status: 'ready', data: r.value }];
    logError('OverviewAppBoxes', `app box ${w} failed`, r.reason);
    return [w, { status: 'failed' }];
  })) as Partial<Record<AppWidget, AppTileState>>;
};
